/**
 * StatePill / PositionPill - colour-coded chips for CAP state +
 * current_position. Used by QueueView rows AND CAPDetailView header
 * so the visual language matches across surfaces.
 *
 * Colour mapping (chosen so the operator can scan a queue at a glance):
 *   draft       slate    "still being authored"
 *   in_review   amber    "needs attention from someone"
 *   approved    emerald  "done"
 *   sent_back   red      "rejected, action required from preparer"
 *
 *   preparer        slate    matches PositionQueueWidget muted style
 *   reviewer    amber    matches in_review
 *   approver    indigo   distinct from approved (state) so they
 *                        don't collide on a CAP that's in_review
 *                        and currently sitting in the approver queue
 */
import type { CAPState, Position } from "shared/api/types";

const STATE_STYLE: Record<CAPState, string> = {
  draft: "bg-slate-200 text-slate-800",
  in_review: "bg-amber-200 text-amber-900",
  approved: "bg-emerald-200 text-emerald-900",
  sent_back: "bg-red-200 text-red-900",
};

const POSITION_STYLE: Record<Position, string> = {
  preparer: "bg-slate-100 text-slate-700 border border-slate-300",
  reviewer: "bg-amber-100 text-amber-800 border border-amber-300",
  approver: "bg-indigo-100 text-indigo-800 border border-indigo-300",
};

const BASE = "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium";

export function StatePill({ state, label }: { state: CAPState; label: string }) {
  return <span className={`${BASE} ${STATE_STYLE[state]}`}>{label}</span>;
}

export function PositionPill({
  position,
  label,
}: {
  position: Position;
  label: string;
}) {
  return <span className={`${BASE} ${POSITION_STYLE[position]}`}>{label}</span>;
}
